import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Calendar,
  Package,
  RefreshCw,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import axios from '../../../axiosConfig';
import { useToast } from '../../../components/ui/Toast';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import useExpiringProducts from '../hooks/useExpiringProducts';
import './ExpiringStockTab.css';

const FILTERS = [
  { id: 'all',     label: 'All',     countKey: 'total' },
  { id: 'EXPIRED', label: 'Expired', countKey: 'expired' },
  { id: 'URGENT',  label: 'Urgent',  countKey: 'urgent' },
  { id: 'WARNING', label: 'Warning', countKey: 'warning' },
  { id: 'INFO',    label: 'Info',    countKey: 'info' },
];

const WINDOWS = [
  { id: 3,  label: '3 days'  },
  { id: 7,  label: '7 days'  },
  { id: 10, label: '10 days' },
  { id: 30, label: '30 days' },
  { id: 60, label: '60 days' },
  { id: 90, label: '90 days' },
];

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  maximumFractionDigits: 0,
});
const fmtMoney = (n) => NAIRA.format(Number(n || 0));

function fmtExpiryPhrase(days) {
  if (days < 0) {
    const ago = Math.abs(days);
    return ago === 1 ? 'Expired yesterday' : `Expired ${ago} days ago`;
  }
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days} days`;
}

/* ─── Module-scope sub-components ──────────────────────────────── */

function Toolbar({ days, onDaysChange, filter, onFilterChange, counts }) {
  return (
    <div className="fin-exp-toolbar">
      <div className="fin-exp-filters" role="tablist" aria-label="Expiry urgency">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`fin-exp-filter${filter === f.id ? ' is-active' : ''}`}
            onClick={() => onFilterChange(f.id)}
          >
            {f.label}
            <span className="fin-exp-filter-count">{counts[f.countKey] ?? 0}</span>
          </button>
        ))}
      </div>

      <label className="fin-exp-window">
        <span className="fin-exp-window-label">Show next</span>
        <select
          className="fin-exp-window-select"
          value={days}
          onChange={(e) => onDaysChange(Number(e.target.value))}
        >
          {WINDOWS.map((w) => (
            <option key={w.id} value={w.id}>{w.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function UrgencyDot({ urgency }) {
  const label = urgency === 'EXPIRED' ? 'Expired'
              : urgency === 'URGENT' ? 'Urgent'
              : urgency === 'WARNING' ? 'Warning'
              : 'Info';
  return (
    <span className={`fin-exp-dot fin-exp-dot--${urgency.toLowerCase()}`} aria-label={label} />
  );
}

/* Detect whether a markdown has already been applied. The backend
   stamps `compareAtPrice` with the pre-discount price when our
   markdown endpoint runs, so compareAtPrice > price is the canonical
   "this is currently on sale" signal. */
function readDiscountState(item) {
  const price = Number(item.price || 0);
  const compareAt = Number(item.compareAtPrice || 0);
  if (compareAt > 0 && compareAt > price) {
    return {
      isDiscounted: true,
      appliedPct: Math.round(((compareAt - price) / compareAt) * 100),
    };
  }
  return { isDiscounted: false, appliedPct: 0 };
}

function StockRow({
  item,
  selected,
  onToggleSelect,
  onMarkdown,
  onMarkExpired,
  canMarkExpired,
}) {
  const tone = item.urgency.toLowerCase();
  const { isDiscounted, appliedPct } = readDiscountState(item);

  return (
    <div className={`fin-exp-row fin-exp-row--${tone}${selected ? ' is-selected' : ''}`}>
      <label className="fin-exp-checkbox">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(item.id || item.productId)}
          aria-label={`Select ${item.name}`}
        />
        <span className="fin-exp-checkbox-box" aria-hidden />
      </label>

      <div className="fin-exp-thumb-wrap">
        {item.image
          ? <img className="fin-exp-thumb" src={item.image} alt="" />
          : <div className="fin-exp-thumb fin-exp-thumb--placeholder" aria-hidden>
              <Package size={18} />
            </div>}
      </div>

      <div className="fin-exp-body">
        <div className="fin-exp-row-head">
          <UrgencyDot urgency={item.urgency} />
          <span className="fin-exp-expiry">{fmtExpiryPhrase(item.daysToExpiry)}</span>
          {item.urgency === 'EXPIRED' && (
            <span className="fin-exp-past-badge">Past expiry</span>
          )}
          <span className="fin-exp-divider" aria-hidden>·</span>
          {item.urgency === 'EXPIRED' ? (
            <span className="fin-exp-suggest">
              Write off to clear from inventory
            </span>
          ) : isDiscounted ? (
            <span className="fin-exp-applied">Already {appliedPct}% off</span>
          ) : (
            <span className="fin-exp-suggest">
              Suggested {item.suggestedDiscountPct}% off
            </span>
          )}
        </div>
        <p className="fin-exp-name" title={item.name}>{item.name}</p>
        <p className="fin-exp-sub">
          {item.quantity} {item.quantity === 1 ? 'unit' : 'units'} at risk
          {(item.expiredQuantity || 0) > 0 && item.urgency !== 'EXPIRED' && (
            <> · <span style={{ color: '#7f1d1d', fontWeight: 600 }}>{item.expiredQuantity} past expiry</span></>
          )}
          {item.costPrice > 0 && ` · Cost ${fmtMoney(item.costPrice)}/u`}
        </p>
      </div>

      <div className="fin-exp-actions">
        {/* Markdown is meaningless on past-expiry stock — it can't be
            sold anyway. Only show on still-sellable rows. */}
        {item.urgency !== 'EXPIRED' && (
          <button
            type="button"
            className="fin-exp-action"
            onClick={() => onMarkdown(item)}
          >
            <Tag size={14} /> {isDiscounted ? 'Adjust markdown' : 'Mark down'}
          </button>
        )}
        {canMarkExpired && (
          <button
            type="button"
            className="fin-exp-action fin-exp-action--danger"
            onClick={() => onMarkExpired(item)}
            title="Write off as expired stock"
          >
            <Trash2 size={14} /> {item.urgency === 'EXPIRED' ? 'Write off' : 'Mark as expired'}
          </button>
        )}
      </div>
    </div>
  );
}

function BulkBar({ count, onApply, onClear }) {
  return (
    <div className="fin-exp-bulkbar" role="region" aria-label="Bulk markdown actions">
      <span className="fin-exp-bulkbar-count">
        {count} selected
      </span>
      <div className="fin-exp-bulkbar-actions">
        <button
          type="button"
          className="fin-exp-bulkbar-clear"
          onClick={onClear}
        >
          <X size={14} /> Clear
        </button>
        <button
          type="button"
          className="fin-exp-bulkbar-apply"
          onClick={onApply}
        >
          <Tag size={14} /> Apply suggested discount
        </button>
      </div>
    </div>
  );
}

function EmptyState({ days }) {
  return (
    <div className="fin-empty">
      <Calendar size={40} className="fin-empty-icon" />
      <h2 className="fin-empty-title">Nothing expires in the next {days} days</h2>
      <p className="fin-empty-sub">
        When products with an expiry date approach their cutoff, they show
        up here so you can mark them down before they spoil. Add an expiry
        date on a product to start tracking.
      </p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

function SkeletonRows({ count = 5 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="fin-exp-row fin-exp-row--skeleton">
      <div className="fin-exp-thumb fin-exp-thumb--skeleton" />
      <div className="fin-exp-body">
        <div className="fin-skel-line fin-skel-line--head" />
        <div className="fin-skel-line fin-skel-line--name" />
        <div className="fin-skel-line fin-skel-line--sub" />
      </div>
      <div className="fin-skel-btn" />
    </div>
  ));
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function ExpiringStockTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [days, setDays] = useState(60);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [working, setWorking] = useState(false);

  const { items, counts, loading, error, refetch, applyDiscount } = useExpiringProducts({ days });

  // Phase 4.2 — "Mark as expired" one-click into the Wastage log.
  // Only available when the merchant has the finance.wastage feature
  // (PRO+). FREE merchants see only the markdown action.
  const { hasAccess: canMarkExpired } = useFeatureAccess('finance.wastage');

  const visibleItems = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((it) => it.urgency === filter);
  }, [items, filter]);

  const toggleSelect = (productId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleSingleMarkdown = async (item) => {
    const { isDiscounted, appliedPct } = readDiscountState(item);
    const message = isDiscounted
      ? `${item.name} is already ${appliedPct}% off. Re-stamp at ${item.suggestedDiscountPct}% off the original price?`
      : `Apply ${item.suggestedDiscountPct}% off ${item.name}? The current price becomes the strike-through value.`;
    const confirmed = await confirm(message, {
      title: isDiscounted ? 'Adjust markdown' : 'Apply markdown',
      confirmText: isDiscounted ? 'Adjust' : 'Mark down',
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (!confirmed) return;

    setWorking(true);
    try {
      const updated = await applyDiscount(item.productId, item.suggestedDiscountPct);
      const verb = isDiscounted ? 'Updated markdown on' : 'Marked down';
      const variantNote = updated?.variantsDiscounted > 0
        ? ` (+ ${updated.variantsDiscounted} ${updated.variantsDiscounted === 1 ? 'variant' : 'variants'})`
        : '';
      toast.success(`${verb} ${item.name} to ${item.suggestedDiscountPct}% off${variantNote}`);
      refetch();
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.message || 'Markdown failed';
      toast.error(errMsg);
    } finally {
      setWorking(false);
    }
  };

  const handleMarkExpired = async (item) => {
    const qty = item.quantity || 0;
    if (qty <= 0) {
      toast.error('No stock left to mark as expired');
      return;
    }
    const isExpiredBucket = item.urgency === 'EXPIRED' || item.bucket === 'EXPIRED';
    const mode = isExpiredBucket ? 'EXPIRED_ONLY' : 'ACTIVE_ONLY';
    const confirmMsg = isExpiredBucket
      ? `Write off all ${qty} past-expiry ${item.name} units? Your sellable stock won't be touched — only the expired bucket.`
      : `Write off ${qty} ${item.name} as expired stock? This pulls from your sellable inventory and logs the loss against your P&L.`;
    const confirmed = await confirm(confirmMsg, {
      title: isExpiredBucket ? 'Write off past-expiry stock?' : 'Mark as expired?',
      confirmText: `Write off ${qty} ${qty === 1 ? 'unit' : 'units'}`,
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (!confirmed) return;

    setWorking(true);
    try {
      await axios.post(`/finance/products/${item.productId}/wastage`, {
        quantity: qty,
        mode,
        // batchIds scopes the write-off to just this row's slice (per
        // expiry-date for UPCOMING rows). Without it a quantity-only
        // request can spill into adjacent slices via FEFO ordering.
        batchIds: Array.isArray(item.batchIds) && item.batchIds.length > 0 ? item.batchIds : undefined,
        notes: isExpiredBucket
          ? 'Past-expiry stock written off from Expiring Stock'
          : 'Marked as expired from Expiring Stock',
      });
      toast.success(`${item.name} written off (${qty} units)`);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to write off');
    } finally {
      setWorking(false);
    }
  };

  const handleBulkApply = async () => {
    const selectedItems = items.filter((it) => selected.has(it.productId));
    if (selectedItems.length === 0) return;

    const alreadyCount = selectedItems.filter((it) => readDiscountState(it).isDiscounted).length;
    const total = selectedItems.length;
    const lines = [
      `Apply each item's suggested discount to ${total} ${total === 1 ? 'product' : 'products'}?`,
    ];
    if (alreadyCount > 0) {
      lines.push(
        `${alreadyCount} ${alreadyCount === 1 ? 'is' : 'are'} already discounted — ` +
        `${alreadyCount === 1 ? 'it' : 'they'} will be re-stamped to the suggested %.`
      );
    }
    lines.push('Current prices become the strike-through values.');

    const confirmed = await confirm(lines.join('\n\n'), {
      title: 'Apply bulk markdown',
      confirmText: `Mark down ${total}`,
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (!confirmed) return;

    setWorking(true);
    let ok = 0;
    let failed = 0;
    for (const it of selectedItems) {
      try {
        await applyDiscount(it.productId, it.suggestedDiscountPct);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setWorking(false);
    clearSelection();
    refetch();
    if (failed === 0) {
      toast.success(`Marked down ${ok} ${ok === 1 ? 'product' : 'products'}`);
    } else {
      toast.warning(`Marked down ${ok}; ${failed} failed`);
    }
  };

  const hasItems = items.length > 0;
  const showEmpty = !loading && !error && !hasItems;

  return (
    <div className="fin-exp">
      {hasItems && (
        <Toolbar
          days={days}
          onDaysChange={(d) => { setDays(d); clearSelection(); }}
          filter={filter}
          onFilterChange={(f) => { setFilter(f); clearSelection(); }}
          counts={counts}
        />
      )}

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {showEmpty && <EmptyState days={days} />}

      {hasItems && (
        <div className="fin-exp-list">
          {visibleItems.map((item) => (
            <StockRow
              key={item.id || item.productId}
              item={item}
              selected={selected.has(item.id || item.productId)}
              onToggleSelect={toggleSelect}
              onMarkdown={handleSingleMarkdown}
              onMarkExpired={handleMarkExpired}
              canMarkExpired={canMarkExpired}
            />
          ))}
          {visibleItems.length === 0 && (
            <div className="fin-exp-empty-filter">
              No products in this urgency bucket. Switch to "All" to see everything.
            </div>
          )}
        </div>
      )}

      {loading && !hasItems && (
        <div className="fin-exp-list">
          <SkeletonRows />
        </div>
      )}

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onApply={handleBulkApply}
          onClear={clearSelection}
        />
      )}

      {working && <div className="fin-exp-busy" aria-live="polite">Working…</div>}
    </div>
  );
}
